// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 65.17.0 — de-identification runs (toolkit row ANON-01).
 *
 * ## What this exists to stop the product asserting
 *
 * `DisposalMethod` offers `'anonymization'` and `DeletionMethod` offers the
 * same, and until this module an operator selecting either produced a record
 * saying the data had been anonymised, with nothing behind it. The programme
 * contract names that exact defect, about that exact word:
 *
 * > *"A text field saying 'submitted,' 'published,' 'signed' or **'anonymized'**
 * > is not completion proof."*
 *
 * A run here records a transformation that was actually performed: over which
 * population, by which method, with what sample, validated or not, approved by
 * a named person, and with a residual re-identification risk **a person judged**.
 *
 * ## Why the platform derives no risk figure
 *
 * Residual re-identification risk depends on what else exists in the world —
 * auxiliary datasets the organisation does not hold and cannot enumerate. A
 * number computed from the transformed data alone would be precise and wrong,
 * and would be read as assurance. The judgement is recorded as a person's, with
 * their reasoning, or it is recorded as absent.
 *
 * ## Its legal footing, which is narrower than the capability
 *
 * **Decree 356/2025/NĐ-CP, Điều 10(6)** — *"Chủ thể dữ liệu cá nhân phải có
 * quyền chỉnh sửa, ẩn danh, xóa hồ sơ nhận dạng, kể cả khi nền tảng lưu trữ
 * lịch sử hành vi"* — imposes it for **artificial-intelligence and metaverse
 * systems**. Outside that scope no installed instrument requires it, which is
 * why the module is opt-in.
 *
 * Sprint 65.16.0 recorded this row as having no legal basis at all. That was
 * measured over the pack, which encodes 12 of Decree 356's 42 articles;
 * 65.16.1 corrected it from the signed source. The product must therefore not
 * describe this capability as "not required by law" without naming the scope.
 */
/**
 * How the transformation was done.
 *
 * Open text is deliberately absent. A method nobody can name is a method nobody
 * can review, and `other` carries `method_note` so the naming is forced to
 * happen somewhere a reader will find it.
 */
export const DEIDENTIFICATION_METHODS = [
    'suppression',
    'generalisation',
    'pseudonymisation',
    'aggregation',
    'noise_addition',
    'hashing_with_deleted_key',
    'other',
];
/**
 * Whether anyone checked the output, and what came back.
 *
 * `not_sampled` and `sample_found_identifiers` are different answers and the
 * difference is the point: one is work not done, the other is work done that
 * failed. A single "incomplete" would hide the second inside the first.
 */
export const DEIDENTIFICATION_SAMPLE_OUTCOMES = [
    'not_sampled',
    'sample_clean',
    'sample_found_identifiers',
];
/**
 * The run's lifecycle. `state_field` for the adopted proof source.
 *
 * `rejected` exists because an approver who cannot refuse is not an approver,
 * and a run that failed its sample must be able to end without being approved.
 */
export const DEIDENTIFICATION_RUN_STATUSES = [
    'draft',
    'awaiting_approval',
    'approved',
    'rejected',
];
//# sourceMappingURL=deidentification.js.map